import { useState, useEffect } from 'react';
import { getHelp } from '../lib/help-content';
import type { HelpItem } from '../types';
import { X, HelpCircle, ImageOff } from 'lucide-react';

interface HelpModalProps {
  helpId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function HelpModal({ helpId, isOpen, onClose }: HelpModalProps) {
  const [help, setHelp] = useState<HelpItem | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (isOpen && helpId) {
      const item = getHelp(helpId);
      setHelp(item || null);
      setImageError(false);
      setImageLoading(true);
    }
  }, [helpId, isOpen]);

  if (!isOpen || !help) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full sm:max-w-lg bg-navy-800 sm:rounded-2xl rounded-t-2xl shadow-2xl border border-navy-700 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="sticky top-0 bg-navy-800/95 backdrop-blur-sm border-b border-navy-700 px-4 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-ocean-600/20 flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-ocean-400" />
            </div>
            <h2 className="text-base font-bold text-navy-100">{help.title}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-navy-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-navy-400" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          <div className="rounded-xl overflow-hidden border border-navy-700 bg-navy-900/50">
            {imageLoading && !imageError && (
              <div className="h-48 sm:h-56 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-ocean-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!imageError ? (
              <img
                src={help.imageUrl}
                alt={help.title}
                className={`w-full h-48 sm:h-56 object-contain bg-navy-900 ${imageLoading ? 'hidden' : 'block'}`}
                onError={() => { setImageError(true); setImageLoading(false); }}
                onLoad={() => setImageLoading(false)}
              />
            ) : (
              <div className="h-48 sm:h-56 flex flex-col items-center justify-center text-navy-500 gap-2">
                <ImageOff className="w-10 h-10" />
                <p className="text-sm">Image unavailable</p>
                <p className="text-xs text-navy-600">Download and replace in /public/help-images/</p>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xs font-bold text-ocean-400 uppercase tracking-wider mb-2">Definition</h3>
            <p className="text-sm text-navy-300 leading-relaxed">{help.definition}</p>
          </div>

          <div className="bg-ocean-600/5 border border-ocean-600/10 rounded-xl p-4">
            <h3 className="text-xs font-bold text-ocean-400 uppercase tracking-wider mb-2">How to Measure</h3>
            <p className="text-sm text-navy-200 leading-relaxed">{help.howTo}</p>
          </div>

          {help.related && help.related.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-navy-500 uppercase tracking-wider mb-2">Related Terms</h3>
              <div className="flex flex-wrap gap-2">
                {help.related.map((r: string) => (
                  <span key={r} className="px-3 py-1.5 bg-navy-700/50 text-navy-300 text-xs rounded-lg border border-navy-700">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-navy-700 bg-navy-800/50">
          <button type="button"
            onClick={onClose}
            className="w-full py-3 bg-ocean-600 hover:bg-ocean-500 text-white rounded-xl font-semibold transition-all active:scale-[0.98]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export function HelpButton({ helpId, onClick }: { helpId: string; onClick: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(helpId); }}
      className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-ocean-600/20 text-ocean-400 hover:bg-ocean-600/30 transition-colors ml-1.5"
      title="Help"
    >
      <HelpCircle className="w-3 h-3" />
    </button>
  );
}
